// src/pages/admin/adminpanel/SalesPanel.jsx
import React, { useState } from 'react';

function usePagination(data, perPage) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PerPageSelector({ perPage, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-secondary">Rows</span>
      <div className="flex gap-1">
        {[5, 10, 20, 50].map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
              ${perPage === n
                ? 'bg-primary text-white'
                : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-3">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

const statusStyle = (s) => ({
  completed:       'bg-emerald-100 text-emerald-700',
  delivered:       'bg-emerald-100 text-emerald-700',
  pending:         'bg-amber-100 text-amber-700',
  confirmed:       'bg-blue-100 text-blue-700',
  out_for_delivery:'bg-purple-100 text-purple-700',
  cancelled:       'bg-red-100 text-red-600',
}[s] || 'bg-gray-100 text-gray-600');

const STATUS_FILTERS = ['all', 'completed', 'delivered', 'pending', 'confirmed', 'out_for_delivery', 'cancelled'];

export default function SalesPanel({ sales, salesStats, loading }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(5);
  const [expandedId, setExpandedId] = useState(null);

  const filtered = sales
    .filter(s => filter === 'all' || s.status === filter)
    .filter(s =>
      !search ||
      s.animal_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.listing_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.buyer_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.seller_name?.toLowerCase().includes(search.toLowerCase())
    );
   
  const pagination = usePagination(filtered, perPage);

  // reset page on filter/search change
  React.useEffect(() => {
    pagination.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search, perPage]);

  const handlePerPageChange = (n) => { setPerPage(n); pagination.reset(); };

  const totalRevenue   = Number(salesStats.total_revenue || 0);
  const completedCount = sales.filter(s => s.status === 'completed').length;
  // const pendingCount   = sales.filter(s => s.status === 'pending').length;
  const cancelledCount = sales.filter(s => s.status === 'cancelled').length;

  return (
    <div className="space-y-6">

      {/* ── Stat cards ────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          {
            label: 'Total Revenue',
            value: `₹${totalRevenue.toLocaleString('en-IN')}`,
            icon:  'payments',
            color: 'text-emerald-600',
            bg:    'bg-emerald-50',
          },
          {
            label: 'Total Orders',
            value: salesStats.total_orders || sales.length,
            icon:  'receipt_long',
            color: 'text-blue-600',
            bg:    'bg-blue-50',
          },
          {
            label: 'Completed',
            value: completedCount,
            icon:  'check_circle',
            color: 'text-primary',
            bg:    'bg-primary/10',
          },
          {
            label: 'Cancelled',
            value: cancelledCount,
            icon:  'cancel',
            color: 'text-red-500',
            bg:    'bg-red-50',
          },
        ].map((s) => (
          <div key={s.label}
            className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10
                       hover:border-primary/20 transition-all flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary">
                {s.label}
              </p>
              <p className="text-3xl font-black text-on-surface mt-1">{s.value ?? '—'}</p>
            </div>
            <span className={`material-symbols-outlined ${s.color} ${s.bg}
                              p-2.5 rounded-xl text-[22px] flex-shrink-0`}>
              {s.icon}
            </span>
          </div>
        ))}
      </div>

      {/* ── Table card ────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                      overflow-hidden">

        {/* Header */}
        <div className="p-6 border-b border-surface-container-high
                        flex flex-wrap justify-between items-center gap-4">
          <div>
            <h3 className="font-bold text-lg">All Sales</h3>
            <p className="text-xs text-secondary mt-0.5">
              {filtered.length} of {sales.length} orders
            </p>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 bg-surface-container-low px-3 py-2
                          rounded-xl border border-outline-variant/20">
            <span className="material-symbols-outlined text-outline text-sm">search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search animal, buyer, seller…"
              className="bg-transparent outline-none text-sm w-52
                         placeholder:text-outline-variant"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="text-outline hover:text-on-surface transition-colors">
                <span className="material-symbols-outlined text-[16px]">close</span>
              </button>
            )}
          </div>
        </div>

        {/* Status filter pills */}
        <div className="px-6 py-3 flex gap-2 flex-wrap border-b
                        border-surface-container-high bg-surface-container-low/50">
          {STATUS_FILTERS.map((s) => {
            const count = s === 'all'
              ? sales.length
              : sales.filter(o => o.status === s).length;
            return (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all
                            ${filter === s
                              ? 'bg-primary text-white shadow-sm'
                              : `${statusStyle(s === 'all' ? null : s)}
                                 ${s === 'all' ? 'bg-gray-100 text-gray-600' : ''}
                                 hover:opacity-80`}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-8 h-8 border-4 border-primary
                            border-t-transparent rounded-full animate-spin" />
          </div>

        /* Empty */
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-secondary">
            <span className="material-symbols-outlined text-4xl text-outline-variant">
              receipt_long
            </span>
            <p className="mt-2 font-semibold">
              {sales.length === 0 ? 'No sales yet' : 'No results found'}
            </p>
            {search && (
              <p className="text-xs mt-1">Try a different search term</p>
            )}
          </div>

        /* Table */
        ) : (
          <>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-surface-container">
            {pagination.slice.map((s) => (
              <div key={s.id} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold">
                      {s.animal_name || s.listing_name || '—'}
                    </p>
                    <p className="text-[10px] text-outline font-mono">#{s.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-primary">
                      ₹{Number(s.amount || s.price_inr || 0).toLocaleString('en-IN')}
                    </p>
                    <span className={`text-[10px] font-bold px-2 py-0.5
                                      rounded-full ${statusStyle(s.status)}`}>
                      {s.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-secondary">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center
                                    justify-center text-blue-700 text-[9px] font-black">
                      {(s.buyer_name || '?')[0].toUpperCase()}
                    </div>
                    {s.buyer_name || '—'}
                  </div>
                  <span className="text-outline">→</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center
                                    justify-center text-emerald-700 text-[9px] font-black">
                      {(s.seller_name || '?')[0].toUpperCase()}
                    </div>
                    {s.seller_name || '—'}
                  </div>
                  <span className="ml-auto text-outline">
                    {s.created_at
                      ? new Date(s.created_at).toLocaleDateString('en-IN')
                      : '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-surface-container-low">
                <tr>
                  {['Order ID', 'Animal', 'Buyer', 'Seller',
                   'Amount', 'Status', 'Date'].map(h => (
                    <th key={h}
                      className="px-5 py-3 text-[10px] font-black uppercase
                                 tracking-widest text-secondary whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container">
                {pagination.slice.map((s) => (
                  <React.Fragment key={s.id}>
                    <tr 
                      onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                      className="hover:bg-surface-container-low/50 transition-colors cursor-pointer">
                    <td className="px-5 py-3 text-xs font-mono text-secondary">#{s.id}</td>
                    <td className="px-5 py-3">
                      <p className="text-sm font-semibold">
                        {s.animal_name || s.listing_name || '—'}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {s.category && (
                          <p className="text-[10px] text-secondary">{s.category}</p>
                        )}
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                          s.order_type === 'meat'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {s.order_type === 'meat' ? 'Meat' : 'Animal'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center
                                        justify-center text-blue-700 text-[10px] font-black
                                        flex-shrink-0">
                          {(s.buyer_name || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-sm text-secondary whitespace-nowrap">
                          {s.buyer_name || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center
                                        justify-center text-emerald-700 text-[10px] font-black
                                        flex-shrink-0">
                          {(s.seller_name || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-sm text-secondary whitespace-nowrap">
                          {s.seller_name || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm font-bold text-primary">
                      ₹{Number(s.amount || s.price_inr || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-[11px]
                                        font-bold ${statusStyle(s.status)}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-secondary whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>
                          {s.created_at
                            ? new Date(s.created_at).toLocaleDateString('en-IN')
                            : '—'}
                        </span>
                        <span className="material-symbols-outlined text-[14px] text-outline">
                          {expandedId === s.id ? 'expand_less' : 'expand_more'}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {expandedId === s.id && (
                  <tr className="bg-surface-container-low/40">
                    <td colSpan={7} className="px-5 py-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {/* Always show these core fields */}
                        {[
                          { label: 'Payment Method',  value: s.payment_method?.toUpperCase() || '—' },
                          { label: 'Payment Status',  value: s.payment_status  || '—' },
                          { label: 'OTP Verified',    value: s.otp_verified ? 'Yes ✓' : 'No' },
                          { label: 'Delivery Name',   value: s.delivery_name   || '—' },
                          { label: 'Delivery Phone',  value: s.delivery_phone  || '—' },
                          { label: 'Delivery Boy',    value: s.delivery_boy_name || '—' },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-white rounded-lg px-3 py-2 border border-outline-variant/10">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-outline">{label}</p>
                            <p className="text-xs font-bold text-on-surface mt-0.5 break-all">{value}</p>
                          </div>
                        ))}
                        
                        {/* Show Razorpay fields only for online payments */}
                        {s.payment_method === 'online' && (
                          <>
                            <div className="bg-white rounded-lg px-3 py-2 border border-outline-variant/10">
                              <p className="text-[9px] font-bold uppercase tracking-widest text-outline">Razorpay Order</p>
                              <p className="text-xs font-bold text-on-surface mt-0.5 break-all">{s.razorpay_order_id || '—'}</p>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 border border-outline-variant/10">
                              <p className="text-[9px] font-bold uppercase tracking-widest text-outline">Razorpay Payment</p>
                              <p className="text-xs font-bold text-on-surface mt-0.5 break-all">{s.razorpay_payment_id || '—'}</p>
                            </div>
                          </>
                        )}
                        
                        {s.delivery_address && (
                          <div className="col-span-2 bg-white rounded-lg px-3 py-2 border border-outline-variant/10">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-outline">Delivery Address</p>
                            <p className="text-xs text-on-surface mt-0.5">{s.delivery_address}</p>
                          </div>
                        )}
                        {s.note && (
                          <div className="col-span-2 bg-white rounded-lg px-3 py-2 border border-outline-variant/10">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-outline">Note</p>
                            <p className="text-xs text-on-surface mt-0.5">{s.note}</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Footer summary */}
        {filtered.length > 0 && (
          <div className="px-6 py-4 border-t border-surface-container-high
                          bg-surface-container-low/50 space-y-3">
            <div className="flex items-center justify-between">
              <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
              <div className="text-right">
                <p className="text-xs text-secondary">
                  {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, filtered.length)} of {filtered.length}
                </p>
                <p className="text-xs font-bold text-primary">
                  Total: ₹{filtered
                    .reduce((sum, s) => sum + Number(s.amount || s.price_inr || 0), 0)
                    .toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            <PaginationBar
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={pagination.setPage}
            />
          </div>
        )}

      </div>
    </div>
  );
}